// @ts-check

import { onDomReady } from '../utils/dom';

/**
 * Video als Artikel-Hintergrund (Player-CE mit Klasse .video-bg: loop, muted, playsinline, Poster).
 *
 * Zwei Betriebsarten, unterschieden am autoplay-Attribut des <video>:
 *  - Sofortstart (Hero): autoplay + preload="auto" im Markup, der Browser lädt und startet selbst.
 *  - Nachladen (weitere Abschnitte): KEIN autoplay, preload="none" → beim Seitenaufbau wird nur das Poster geladen.
 *    Erst wenn der Abschnitt in die Nähe des Viewports scrollt (LOAD_MARGIN), wird die Quelle geladen und gestartet.
 *
 * Für beide gilt: außerhalb des Viewports pausieren (spart Decoder/Akku), beim Zurückscrollen weiterlaufen. Dieselbe
 * Datei in mehreren Abschnitten kostet so nie einen zweiten aktiven Decoder und beim Erstaufbau kein zusätzliches Byte.
 *
 * Das Poster ist der Fallback: der Browser zeigt es, bis das erste Frame gerendert ist. Diese Komponente sorgt dafür,
 * dass es bei schlechter Verbindung dabei bleibt:
 *  - Datensparmodus oder effectiveType 2G/3G (Network Information API, nur Chromium) oder prefers-reduced-motion
 *    → Video gar nicht laden. Achtung: `connection.downlink` ist KEIN brauchbares Signal – ohne Messdaten meldet
 *    Chrome den 4g-Standardwert 1,6 Mbit/s (±10 % Rauschen), auch auf schnellen Leitungen; und gedrosseltes
 *    „Fast 3G“ erscheint als „4g“. Langsame Leitungen erkennt deshalb erst der Ruckel-Zähler unten.
 *  - Startet die Wiedergabe nicht innerhalb von START_TIMEOUT oder stockt sie länger als STALL_TIMEOUT → Quelle
 *    entfernen und zum Poster zurückkehren (kein weiterer Download).
 *  - Stockt sie MAX_STALLS-mal länger als MIN_STALL, ohne dazwischen STALL_RESET lang durchzulaufen (Dauer-Ruckeln,
 *    greift auch in Firefox/Safari ohne Network Information API) → ebenfalls Poster. MIN_STALL filtert das
 *    `waiting` von wenigen Millisekunden, das Chrome beim Schleifen-Neustart (loop) feuert.
 * Das Poster selbst wird immer geladen (das poster-Attribut kennt kein Lazy-Loading) – es muss klein sein
 * (JPG, ≤ 300 KB); Contao skaliert das Player-Poster nicht.
 * Zustandsklassen am Element: .video-bg--playing, .video-bg--fallback.
 */

const START_TIMEOUT = 8000;
const STALL_TIMEOUT = 6000;
const MAX_STALLS = 3; // Puffer-Pausen (waiting) nach dem Start, dann Poster
const MIN_STALL = 500; // kürzere Pausen zählen nicht (Schleifen-Neustart: ~5 ms)
const STALL_RESET = 10000; // so lange störungsfrei gelaufen → Zähler zurück
const LOAD_MARGIN = '50% 0px'; // Nachladen ab einer halben Viewporthöhe vor dem Abschnitt

function isSlowConnection() {
    // @ts-ignore – Network Information API (Chromium/Android), in anderen Browsern undefined
    const connection = navigator.connection;

    if (!connection) {
        return false;
    }

    return connection.saveData === true || ['slow-2g', '2g', '3g'].includes(connection.effectiveType);
}

/**
 * @param {HTMLVideoElement} video
 */
function initVideoBg(video) {
    const wrapper = /** @type {HTMLElement} */ (video.closest('.video-bg') ?? video.parentElement);
    const lazy = !video.hasAttribute('autoplay');
    let timer = 0;
    let resetTimer = 0;
    let stallTimer = 0;
    let stalls = 0;
    let started = false;
    let hasPlayed = false;
    let failed = false;

    const fallback = () => {
        clearTimeout(timer);
        clearTimeout(resetTimer);
        clearTimeout(stallTimer);
        failed = true;
        wrapper.classList.remove('video-bg--playing');
        wrapper.classList.add('video-bg--fallback');

        video.pause();
        video.removeAttribute('autoplay');
        video.removeAttribute('src');
        video.querySelectorAll('source').forEach((source) => source.remove());
        video.load(); // bricht laufende Downloads ab, Poster bleibt sichtbar
    };

    if (isSlowConnection() || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        fallback();
        return;
    }

    const armTimer = (duration) => {
        clearTimeout(timer);
        timer = window.setTimeout(fallback, duration);
    };

    const countStall = () => {
        stallTimer = 0;
        stalls += 1;

        if (stalls >= MAX_STALLS) {
            fallback();
        }
    };

    video.addEventListener('playing', () => {
        clearTimeout(timer);
        clearTimeout(stallTimer); // Pause war kürzer als MIN_STALL → zählt nicht
        stallTimer = 0;
        hasPlayed = true;
        wrapper.classList.add('video-bg--playing');

        clearTimeout(resetTimer);
        resetTimer = window.setTimeout(() => {
            stalls = 0;
        }, STALL_RESET);
    });

    // Puffer leer → Zeit geben, sonst Poster; wiederholtes Stocken zählt (waiting vor dem ersten Start nicht)
    video.addEventListener('waiting', () => {
        if (hasPlayed && !stallTimer) {
            clearTimeout(resetTimer);
            stallTimer = window.setTimeout(countStall, MIN_STALL);
        }

        armTimer(STALL_TIMEOUT);
    });
    video.addEventListener('stalled', () => armTimer(STALL_TIMEOUT));
    video.addEventListener('error', fallback);
    video.querySelectorAll('source').forEach((source) => source.addEventListener('error', fallback));

    const start = () => {
        if (failed) {
            return;
        }

        if (!started) {
            started = true;

            if (lazy) {
                video.preload = 'auto'; // ab jetzt darf der Browser puffern (Schleife)
            }
        }

        if (!hasPlayed) {
            armTimer(START_TIMEOUT);
        }

        // Autoplay kann trotz muted verweigert werden (z. B. Energiesparmodus) → dann Poster
        video.play().catch(fallback);
    };

    const stop = () => {
        if (failed || !started) {
            return;
        }

        clearTimeout(timer); // pausiert ist kein Stall
        video.pause();
    };

    if (!('IntersectionObserver' in window)) {
        start();
        return;
    }

    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => (entry.isIntersecting ? start() : stop()));
        },
        { rootMargin: LOAD_MARGIN }
    );

    observer.observe(wrapper);
}

onDomReady(() => {
    document.querySelectorAll('.video-bg video').forEach((video) => initVideoBg(/** @type {HTMLVideoElement} */ (video)));
});
